/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment list item styles
 *
 * @file assignmentListItemStyles.js
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import { styled } from '@mui/material/styles';
import CommonStyles from '../../../assets/styles/CommonStyles';
import mixins from '../../../assets/styles/mixins.scss';
import { createScopedClasses, createScopedStyles } from '../../../assets/styles/createScopedStyles';

const hoverBorderColor = '#8e84dc';
const inlineEditBorderColor = mixins.amethystColorV2;

const assignmentListItemStyles = {
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    borderRadius: 12,
    backgroundColor: mixins.chalkWhiteFont,
    border: '2px solid transparent',
    boxSizing: 'border-box',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',

    '&$itemHasSections:hover:not($itemInlineEditing):not($itemSelected):not($itemScheduleBuilder)': {
      backgroundColor: mixins.feedBackColor,
      borderColor: hoverBorderColor,
      '& $editButton': {
        opacity: 1
      }
    },

    [mixins['mlg-max']]: {
      display: 'grid',
      gridTemplateColumns: '64px minmax(0, 1fr) 24px',
      columnGap: 8,
      rowGap: 8,
      alignItems: 'start',
      width: '100%',
      minWidth: 0,
      maxWidth: '100%'
    }
  },
  itemHasSections: {},
  itemClickToEdit: {
    '&:hover:not($itemInlineEditing):not($itemSelected) $itemTitle': {
      color: mixins.zambezi
    }
  },
  itemClearDataOnHover: {
    '&:hover $clearDataButtonHoverReveal, &$itemInlineEditing $clearDataButtonHoverReveal': {
      opacity: 1,
      pointerEvents: 'auto'
    }
  },
  itemScheduleBuilder: {
    '&$itemSchedulePopulated:hover': {
      backgroundColor: mixins.feedBackColor,
      borderColor: hoverBorderColor,
      '& $dateBadge, & $dateBadgeButton': {
        backgroundColor: mixins.feedBackColor
      }
    },
    '&$itemScheduleEmpty:hover, &$itemScheduleEmpty$itemEmptySelected': {
      borderColor: inlineEditBorderColor,
      '& $dragZone': {
        borderColor: inlineEditBorderColor
      },
      '& $emptySlotRemoveButton': {
        opacity: 1,
        pointerEvents: 'auto'
      },
      '& $scheduleDateEditIcon': {
        opacity: 1,
        visibility: 'visible'
      }
    },

    [mixins['mlg-max']]: {
      '& $sectionsRow': {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4
      },
      '& $sectionsChipsScroll': {
        width: '100%',
        flex: 'none'
      }
    }
  },
  itemScheduleEmpty: {
    cursor: 'default'
  },
  itemSchedulePopulated: {
    cursor: 'pointer'
  },
  itemEmptySelected: {},
  itemSelected: {
    borderColor: mixins.amethystColorV2,
    '& $editButton': {
      opacity: 1
    }
  },
  itemInlineEditing: {
    borderColor: inlineEditBorderColor,
    backgroundColor: mixins.feedBackColor,
    '&:hover': {
      backgroundColor: mixins.feedBackColor,
      borderColor: inlineEditBorderColor
    },
    '& $dateBadge, & $dateBadgeButton': {
      backgroundColor: mixins.feedBackColor
    }
  },
  itemPopulatedEditing: {
    backgroundColor: mixins.feedBackColor,
    borderColor: hoverBorderColor,
    '&:hover': {
      backgroundColor: mixins.feedBackColor,
      borderColor: hoverBorderColor
    },
    '& $dateBadge, & $dateBadgeButton': {
      backgroundColor: mixins.feedBackColor
    },
    '& $clearDataButton': {
      opacity: 1,
      pointerEvents: 'auto'
    },
    '& $main': {
      alignItems: 'center'
    },
    '& $actionsCell': {
      alignSelf: 'center'
    },

    [mixins['mlg-max']]: {
      gridTemplateColumns: '64px minmax(0, 1fr)',
      '& $actionsCell': {
        display: 'none'
      },
      '& $itemContent': {
        display: 'flex',
        flexDirection: 'column',
        gap: 4
      },
      '& $scheduleTitleEditRow, & $titleRow': {
        width: '100%'
      }
    }
  },
  checkboxButton: {
    padding: 0,
    flexShrink: 0,
    '& svg': {
      fontSize: 18,
      color: mixins.amethyst
    },

    [mixins['mlg-max']]: {
      gridColumn: '1 / -1',
      gridRow: 1
    }
  },
  main: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    gap: 16,
    minWidth: 0,

    [mixins['mlg-max']]: {
      display: 'contents'
    }
  },
  middleContent: {
    display: 'flex',
    flex: 1,
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
    minWidth: 0,

    // Growable middle cells share remaining horizontal space
    '& > $middleCell, & > $topicsCell': {
      flex: '1 1 0',
      minWidth: 0
    },

    '& > $publishStatusCell': {
      flex: '1 1 180px',
      minWidth: 0
    },

    '& > $itemContent': {
      flex: '0 1 420px',
      minWidth: 0,
      maxWidth: 420
    },

    '&:has($sectionsRow) > $itemContent:not(:only-child)': {
      flex: '0 0 420px',
      width: 420
    },

    // Title-only: stack title and completion at full width
    '&:has($itemContent:not(:has($sectionsRow)))': {
      alignItems: 'flex-start',

      '& > $itemContent, & > $middleCell': {
        flex: '1 1 100%',
        width: '100%',
        maxWidth: 'none'
      }
    },

    // Sole child fills the row
    '& > :only-child': {
      flex: '1 1 100%',
      width: '100%',
      maxWidth: 'none'
    },

    // Drag zone fills remaining space beside a compact title block
    '&:has($dragZone) > $itemContent': {
      flex: '0 0 auto',
      width: 'auto',
      maxWidth: 420
    },

    '&:has($dragZone) > $dragZone': {
      flex: '1 1 0',
      minWidth: 0,
      maxWidth: 'none'
    },

    [mixins['mlg-max']]: {
      display: 'contents',

      '& > $middleCell, & > $topicsCell, & > $publishStatusCell': {
        flex: 'none'
      },

      '& > $itemContent': {
        flex: 'none',
        width: 'auto',
        maxWidth: 'none'
      },

      '&:has($sectionsRow) > $itemContent:not(:only-child)': {
        flex: 'none',
        width: 'auto',
        maxWidth: 'none'
      },

      '&:has($dragZone) > $itemContent': {
        flex: 'none',
        width: 'auto',
        maxWidth: 'none'
      }
    }
  },
  middleContentFixedTitle: {
    alignItems: 'flex-start',

    '& > $itemContent': {
      flex: '0 0 420px',
      width: 420,
      maxWidth: '100%',
      minWidth: 0
    },

    '& $titleRow': {
      minWidth: 0,
      width: '100%'
    },

    '& $titleGroup': {
      minWidth: 0,
      flex: '1 1 auto'
    }
  },
  middleContentScheduleBuilder: {
    alignItems: 'stretch',

    '& > $itemContent': {
      flex: '1 1 auto',
      width: '100%',
      maxWidth: 'none'
    },

    '&:has($dragZone) > $dragZone': {
      flex: '1 1 0',
      minWidth: 0,
      maxWidth: 'none',
      alignSelf: 'stretch'
    }
  },
  dateBadge: {
    width: 72,
    height: 80,
    borderRadius: 8,
    border: `1px solid ${mixins.color_elevation_alpha_12}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    flexShrink: 0,
    boxSizing: 'border-box',
    padding: '8px 4px',
    position: 'relative',
    backgroundColor: mixins.chalkWhiteFont,
    transition: 'background-color 0.15s ease',

    [mixins['mlg-max']]: {
      gridColumn: 1,
      gridRow: 1,
      width: 64,
      minWidth: 64,
      maxWidth: 64
    }
  },
  dateBadgeButton: {
    appearance: 'none',
    backgroundColor: mixins.chalkWhiteFont,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: mixins.feedBackColor
    },
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: 2
    }
  },
  dateBadgeWeekday: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '14px',
    color: mixins.zambezi,
    textTransform: 'uppercase'
  },
  dateBadgeMonth: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '16px',
    color: mixins.color_text_primary
  },
  dateBadgeDay: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 24,
    fontWeight: mixins.fontWeight500,
    lineHeight: '32px',
    letterSpacing: '-0.48px',
    color: mixins.color_text_primary
  },
  addDateBadge: {
    width: 72,
    height: 80,
    borderRadius: 8,
    border: `1px solid ${mixins.color_elevation_alpha_12}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    flexShrink: 0,
    boxSizing: 'border-box',
    padding: '8px 4px',
    backgroundColor: mixins.chalkWhiteFont,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: mixins.feedBackColor
    },

    [mixins['mlg-max']]: {
      gridColumn: 1,
      gridRow: 1,
      width: 64,
      minWidth: 64,
      maxWidth: 64
    }
  },
  addDateIcon: {
    fontSize: 24,
    color: mixins.color_text_primary
  },
  addDateLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '14px',
    color: mixins.color_text_primary,
    textAlign: 'center'
  },
  sectionChipMuted: {
    backgroundColor: mixins.feedBackColor,
    color: '#2E2E2E'
  },
  itemContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,

    [mixins['mlg-max']]: {
      gridColumn: 2,
      gridRow: 1,
      minWidth: 0,
      maxWidth: 'none',
      width: 'auto'
    }
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 24
  },
  statusIcon: {
    fontSize: 18,
    flexShrink: 0
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0
  },
  scheduleTitleGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 4,
    minWidth: 0,
    flex: '1 1 auto',
    width: '100%'
  },
  itemTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary,
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    [mixins['md-sm-maxwidth']]: {
      fontSize: 18,
      lineHeight: '25px',
      letterSpacing: '-0.36px'
    },

    [mixins['mlg-max']]: {
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'unset',
      fontSize: 18,
      lineHeight: '25px'
    }
  },
  itemTitleMuted: {
    color: mixins.zambezi
  },
  scheduleTitleLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.greyScaleColor
  },
  scheduleTitleEditRow: {
    display: 'flex',
    width: '100%',
    minWidth: 0
  },
  scheduleTitleInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    width: '100%',
    minWidth: 0
  },
  scheduleTitleInlineDelete: {
    display: 'none',
    flexShrink: 0,

    [mixins['mlg-max']]: {
      display: 'flex',
      alignItems: 'center'
    }
  },
  scheduleTitleInput: {
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 4,
    padding: '8px 12px',
    backgroundColor: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.amethyst}`,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    color: mixins.zambezi,
    outline: 'none',
    letterSpacing: '-0.4px',

    '&:focus': {
      borderColor: mixins.amethystColorV2
    }
  },
  editButton: {
    padding: 4,
    opacity: 0,
    transition: 'opacity 0.15s ease',
    flexShrink: 0,
    '& svg': {
      fontSize: 18
    }
  },
  sectionsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    minWidth: 0,
    width: '100%'
  },
  sectionsChipsScroll: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flex: '1 1 auto',
    minWidth: 0,
    flexWrap: 'nowrap',
    overflowX: 'auto',
    ...CommonStyles().scrollContainer({
      scrollbarWidth: 4,
      scrollbarColor: '#e8e6f3 transparent',
      borderRadius: 8,
      overflowY: 'hidden'
    }),

    [mixins['mlg-max']]: {
      paddingBottom: 8,
      paddingRight: 4,
      marginBottom: 2,

      '&::-webkit-scrollbar': {
        height: 4
      }
    }
  },
  sectionsPrefix: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.greyScaleColor,
    flexShrink: 0,
    paddingTop: 2
  },
  sectionChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    backgroundColor: mixins.feedBackColor,
    borderRadius: 4,
    padding: '2px 4px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.timeIconColor,
    flexShrink: 0,
    maxWidth: 160,
    minWidth: 0,

    [mixins['ml-max']]: {
      maxWidth: 120
    }
  },
  sectionChipLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  },
  noQuestionsWarningRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px'
  },
  noQuestionsWarningIcon: {
    width: 18,
    height: 18,
    display: 'block',
    flexShrink: 0
  },
  sectionChipRemoveButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    border: 'none',
    borderRadius: '50%',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    color: mixins.color_text_primary,
    flexShrink: 0,
    '& svg': {
      fontSize: 12
    },
    '&:hover': {
      backgroundColor: mixins.color_elevation_alpha_12
    }
  },
  middleCell: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    padding: '0 8px',

    [mixins['mlg-max']]: {
      gridColumn: '1 / 3',
      gridRow: 2,
      width: '100%',
      maxWidth: '100%'
    }
  },
  progressBarWrapper: {
    width: '100%',

    '& *': {
      boxSizing: 'border-box'
    }
  },
  topicsCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    alignContent: 'flex-start',
    minWidth: 0,
    overflow: 'hidden',

    [mixins['mlg-max']]: {
      gridColumn: '1 / 3',
      gridRow: 3,
      width: '100%',
      maxWidth: '100%'
    }
  },
  topicChip: {
    backgroundColor: mixins.feedBackColor,
    borderRadius: 4,
    padding: '2px 4px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.accentBrown,
    flexShrink: 0
  },
  publishStatusCell: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '6px 8px',
    minWidth: 0,
    flex: '1 1 0',
    maxWidth: '100%',

    [mixins['mlg-max']]: {
      gridColumn: '1 / 3',
      gridRow: 2,
      width: '100%',
      maxWidth: '100%'
    }
  },
  publishStatusIcon: {
    fontSize: 24,
    flexShrink: 0
  },
  publishStatusLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    color: mixins.color_text_primary,
    margin: 0,
    minWidth: 0,
    flex: '1 1 auto',
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  },
  dragZone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    minHeight: 80,
    border: `2px dashed ${mixins.color_elevation_alpha_12}`,
    borderRadius: 12,
    padding: '8px 16px',
    position: 'relative',
    boxSizing: 'border-box'
  },
  dragZoneActive: {
    borderColor: mixins.amethyst,
    backgroundColor: mixins.badgeBgColor
  },
  dragZoneInlineEditing: {
    borderColor: inlineEditBorderColor
  },
  dragZoneRemoveButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    border: 'none',
    borderRadius: '50%',
    backgroundColor: mixins.color_elevation_alpha_12,
    padding: 0,
    cursor: 'pointer',
    color: mixins.color_text_primary,
    '& svg': {
      fontSize: 16
    }
  },
  dragZoneIcon: {
    fontSize: 24,
    flexShrink: 0
  },
  dragZoneLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '22px',
    color: mixins.color_text_primary,
    margin: 0
  },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    minHeight: 40,

    [mixins['mlg-max']]: {
      gridColumn: 3,
      gridRow: '1 / -1',
      alignSelf: 'end',
      justifySelf: 'end',
      minHeight: 'unset',
      width: 24
    }
  },
  removeButton: {
    padding: 8,
    flexShrink: 0,
    '& svg': {
      fontSize: 16,
      color: mixins.amethyst
    }
  },
  clearDataButton: {
    padding: 8,
    flexShrink: 0,
    '& svg': {
      fontSize: 20,
      color: mixins.color_text_primary
    }
  },
  clearDataButtonHoverReveal: {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.15s ease'
  },
  emptySlotRemoveButton: {
    padding: 8,
    flexShrink: 0,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.15s ease',
    '& svg': {
      fontSize: 16,
      color: mixins.color_text_primary
    }
  },
  scheduleDateEditIcon: {
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.15s ease'
  },
  dueDateEditableIconContainer: {
    position: 'absolute',
    left: -4,
    top: -4,
    borderRadius: '50%',
    backgroundColor: mixins.quaternary,
    padding: 2,
    boxSizing: 'border-box',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dueDateEditableIcon: {
    fontSize: 8,
    width: 8,
    height: 8
  },
  dueDateExtensionInfoContainer: {
    position: 'absolute',
    right: -4,
    top: -4,
    borderRadius: '50%',
    backgroundColor: mixins.chalkWhiteFont,
    padding: 2,
    boxSizing: 'border-box',
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'default',
    zIndex: 1,
    border: 'none',
    appearance: 'none'
  },
  dueDateExtensionInfoIcon: {
    fontSize: 16,
    width: 16,
    height: 16,
    color: mixins.color_text_primary
  }
};

export const classes = createScopedClasses('aet-assignment-list-item', assignmentListItemStyles);

export const scopedStyles = createScopedStyles(assignmentListItemStyles, classes);

export const AssignmentListItemStylesRoot = styled('div')(scopedStyles);

export default assignmentListItemStyles;
